import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { categoriesService, ageGroupsService } from "../../services";
import { getAgeGroupColors } from "../../utils/ageGroupColors";
import GenericSelectTailwind from "./GenericSelectTailwind";

const CategoryFormTailwind = forwardRef(({ 
  category = null, 
  onSave, 
  onCancel, 
  isSaving = false 
}, ref) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    type: "",
    ageGroupIds: [],
    description: ""
  });
  const [ageGroups, setAgeGroups] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);

  // Expose handleSubmit function to parent
  useImperativeHandle(ref, () => ({
    handleSubmit: () => {
      if (validateForm()) {
        onSave(formData);
      }
    }
  }));

  // Lade Altersgruppen beim ersten Laden
  useEffect(() => {
    loadAgeGroups();
  }, []);

  // Setze Formulardaten wenn Kategorie zum Bearbeiten übergeben wird
  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name || "",
        type: category.type || "",
        ageGroupIds: category.ageGroupIds || category.ageGroups?.map(ag => typeof ag === 'object' ? ag.id : ag) || [],
        description: category.description || ""
      });
    }
  }, [category]);

  const loadAgeGroups = async () => {
    try {
      const response = await ageGroupsService.getAllAgeGroups();
      if (response.success) {
        setAgeGroups(response.data);
      }
    } catch (error) {
      console.error("Fehler beim Laden der Altersgruppen:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Entferne Fehler für dieses Feld
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleAgeGroupToggle = (ageGroupId) => {
    setFormData(prev => ({
      ...prev,
      ageGroupIds: prev.ageGroupIds.includes(ageGroupId)
        ? prev.ageGroupIds.filter(id => id !== ageGroupId)
        : [...prev.ageGroupIds, ageGroupId]
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name ist erforderlich";
    }

    if (!formData.type) {
      newErrors.type = "Typ ist erforderlich";
    }

    if (formData.ageGroupIds.length === 0) {
      newErrors.ageGroupIds = "Mindestens eine Altersgruppe muss ausgewählt werden";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const typeOptions = [
    { value: "Handform", label: "Handform" },
    { value: "Waffenform", label: "Waffenform" },
    { value: "Offene Form", label: "Offene Form" },
    { value: "Partnerform", label: "Partnerform" }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary dark:border-primary-light"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400">Lade Altersgruppen...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-slate-900 dark:text-slate-100 mb-2">
          Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          className={`w-full px-3 py-2.5 border rounded-md focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400 ${
            errors.name ? 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/30' : 'border-slate-200 dark:border-slate-700'
          }`}
          placeholder="Kategorie-Name eingeben"
        />
        {errors.name && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.name}</p>
        )}
      </div>

      {/* Typ */}
      <div>
        <label className="block text-sm font-medium text-slate-900 dark:text-slate-100 mb-2">
          Typ <span className="text-red-500">*</span>
        </label>
        <GenericSelectTailwind
          value={formData.type}
          onChange={(value) => handleInputChange('type', value)}
          options={typeOptions.map(option => ({
            ...option,
            icon: option.value === "Handform" ? <i className="fas fa-hand text-orange-600"></i> :
                  option.value === "Waffenform" ? <i className="fas fa-sword text-gray-600"></i> :
                  option.value === "Offene Form" ? <i className="fas fa-star text-yellow-600"></i> :
                  <i className="fas fa-users text-blue-600"></i>
          }))}
          placeholder="Typ auswählen"
          leftIcon={<i className="fas fa-tag text-slate-400 dark:text-slate-500"></i>}
        />
        {errors.type && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.type}</p>
        )}
      </div>

      {/* Altersgruppen */}
      <div>
        <label className="block text-sm font-medium text-slate-900 dark:text-slate-100 mb-2">
          Altersgruppen <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-2 gap-3">
          {ageGroups.map((ageGroup) => {
            const isSelected = formData.ageGroupIds.includes(ageGroup.id);
            const colors = getAgeGroupColors(ageGroup.name);
            
            return (
              <button
                key={ageGroup.id}
                type="button"
                onClick={() => handleAgeGroupToggle(ageGroup.id)}
                className={`p-3 rounded-md border-2 transition-all duration-200 text-left ${
                  isSelected
                    ? `${colors.bg} text-white border-transparent shadow-md`
                    : `bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:shadow-sm`
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${
                    isSelected ? 'bg-white/30' : colors.bg
                  }`}></div>
                  <span className="font-medium text-sm">{ageGroup.name}</span>
                </div>
              </button>
            );
          })}
        </div>
        {errors.ageGroupIds && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.ageGroupIds}</p>
        )}
      </div>

      {/* Beschreibung */}
      <div>
        <label className="block text-sm font-medium text-slate-900 dark:text-slate-100 mb-2">
          Beschreibung
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          rows={3}
          className="w-full px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-md focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400 resize-none"
          placeholder="Optionale Beschreibung der Kategorie"
        />
      </div>

      {/* Vorschau */}
      {formData.name && formData.type && (
        <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
          <h4 className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-2">Vorschau:</h4>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-primary dark:bg-primary-light text-white text-xs font-medium rounded">
              {formData.type}
            </span>
            <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{formData.name}</span>
            {formData.ageGroupIds.length > 0 && (
              <div className="flex gap-1">
                {formData.ageGroupIds.map((ageGroupId) => {
                  const ageGroup = ageGroups.find(ag => ag.id === ageGroupId);
                  if (!ageGroup) return null;
                  const colors = getAgeGroupColors(ageGroup.name);
                  return (
                    <span
                      key={ageGroupId}
                      className={`px-2 py-1 text-xs font-medium rounded ${colors.bgLight} ${colors.text}`}
                    >
                      {ageGroup.name}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

export default CategoryFormTailwind;
